<template>
  <el-form
    :label-width="labelWidth"
    :model="fM"
    ref="sFormRef"
  >
    <el-row>
      <!-- 普通表单项 -->
      <el-col
        v-for="(item, index) in fS"
        :key="index"
        :sm="{span: 24 , offset: 0}"
        :xs="{span: 24 , offset: 0}"
        :md="calculateMD(item)"
      >
        <el-form-item
          :label="item.label"
          :prop="item.prop"
        >
          <template v-if="item.isDynamicDateType">
            <div class="dynamic-date-picker-group">
              <el-select
                class="date-type-select"
                v-model="item.datePickerType"
                @change="handleDateTypeChange(item)"
              >
                <el-option
                  v-for="(dateType, typeIndex) in item.dateTypeOptions"
                  :key="typeIndex"
                  :label="dateType.label"
                  :value="dateType.value"
                />
              </el-select>
              <el-date-picker
                :is="item.component"
                :type="getInputType(item)"
                :placeholder="item.placeholder"
                :disabled="item.disabled"
                v-model="fM[item.prop]"
                style="width:100%;min-width:230px"
                clearable
              />
              <span
                v-if="item.suffix"
                class="suffix-text"
              >{{ item.suffix }}</span>
            </div>
          </template>
          <template v-else>
            <div :class="{'input-with-suffix': item.suffix}">
              <component
                :is="item.component"
                :type="item.type"
                :placeholder="item.placeholder"
                :filterable="item.filterable"
                :remote="item.remote"
                :remote-method="item.remoteMethod"
                :loading="item.loading"
                :disabled="item.disabled"
                style="width:100%;min-width:230px"
                v-model="fM[item.prop]"
                clearable
              >
                <template v-if="item.component==='el-select'">
                  <el-option
                    v-for="(optionItem, optionIndex) in item.options"
                    :key="optionIndex"
                    :label="optionItem.label"
                    :value="optionItem.value"
                  />
                </template>
              </component>
              <span
                v-if="item.suffix"
                class="suffix-text"
              >{{ item.suffix }}</span>
            </div>
          </template>
        </el-form-item>
      </el-col>
      <el-col
        class="button-group"
        :sm="{span: 24 , offset: 0}"
        :xs="{span: 24 , offset: 0}"
        :md="{span: 4 , offset: bOffset}"
      >
        <el-button
          :disabled="resetDisabled"
          @click="resetEmit"
        >
          {{ t(resetButton) }}
        </el-button>
        <el-button
          type="primary"
          :disabled="submitDisabled"
          @click="submitEmit"
        >
          {{ t(submitButton) }}
        </el-button>
      </el-col>
    </el-row>
  </el-form>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useClickLock } from '@/utils/useClickLock'
import resize from '@/layout/resize'
import { DeviceType } from '@/store/modules/app/state'

export default defineComponent({
  props: {
    formModel: {
      type: Object,
      default: () => {
        return null
      }
    },
    formStruct: {
      type: Array,
      default: () => []
    },
    formRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    submitButton: {
      type: String,
      required: false,
      default: 'common.submit'
    },
    resetButton: {
      type: String,
      required: false,
      default: 'common.reset'
    },
    labelWidth: {
      type: String,
      required: false,
      default: '150px'
    }
  },
  emits: ['submit', 'reset', 'update:formModel'],
  setup(props, context) {
    const { t } = useI18n()
    const sFormRef = ref()
    const fM = ref(props.formModel)
    const fS = ref(props.formStruct)
    const { disabled: submitDisabled, lock: submitLock } = useClickLock(2000)
    const { disabled: resetDisabled, lock: resetLock } = useClickLock(2000)
    const { device } = resize()
    const bOffset = computed(() => {
      if (device.value === DeviceType.Mobile) {
        return 0
      } else if ((props.formStruct.length % 2) === 0) {
        return 2
      }
      return 11
    })
    const submitEmit = () => {
      submitLock(() => {
        context.emit('submit')
      })
    }
    const resetEmit = () => {
      resetLock(() => {
        sFormRef.value.resetFields()
        context.emit('reset')
      })
    }
    const getInputType = (item: any) => {
      if (item.component === 'el-date-picker' && item.datePickerType === 0) {
        return 'date'
      } else if (item.component === 'el-date-picker' && item.datePickerType === 1) {
        return 'date'
      } else if (item.component === 'el-date-picker' && item.datePickerType === 2) {
        return 'daterange'
      }
      return item.type
    }
    const handleDateTypeChange = (item: any) => {
      // 切换类型，清空原来的日期
      fM.value[item.prop] = null

      if (item.datePickerType === 0) {
        item.suffix = '后'
      } else if (item.datePickerType === 1) {
        item.suffix = '前'
      } else {
        item.suffix = null
      }
    }
    const calculateMD = (item: any) => {
      if (item.component === 'el-date-picker' && item.isDynamicDateType) {
        return { span: 9, offset: 0 }
      } else {
        return { span: 7, offset: 2 }
      }
    }
    return {
      t,
      fM,
      fS,
      sFormRef,
      submitDisabled,
      resetDisabled,
      bOffset,
      calculateMD,
      handleDateTypeChange,
      getInputType,
      submitEmit,
      resetEmit
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.input-with-suffix {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 230px;
}

.input-with-suffix :deep(.el-input),
.input-with-suffix :deep(.el-date-editor),
.input-with-suffix :deep(.el-select) {
  flex: 1;
}

.suffix-text {
  flex: 0 0 auto; /* 不伸缩 */
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
  min-width: 20px;
  margin-left: 5px;
}

.dynamic-date-picker-group {
  display: flex;
  align-items: center;
  width: 100%;
}

/* 日期类型选择器 - 固定宽度 */
.date-type-select {
  flex: 0 0 125px; /* 不伸缩，固定 120px */
  width: 125px;
  margin-right: 10px;
}

@media (max-width: 992px) {
  .button-group {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
